import { parseNumberStrict } from 'shouldve-been-js';
import type AppTheme from '../Enums/AppTheme';
import LocalStorageKey from '../Enums/LocalStorageKey';
import type Mode from '../Enums/Mode';
import TokenType from '../Enums/TokenType';
import CurrentUser from '../Models/CurrentUserModel';
import { datetimeStandardParser, datetimeToApiString } from '../Utils/DateTimeParser';
import ApiService from './ApiService';

class LocalStorageService {

    private _id: string;

    public constructor(id: string) {
        this._id = id;
    }

    public get id(): string {
        return this._id;
    }

    public set id(value: string) {
        this._id = value;
    }

    public static isLocalStorageAvailable(): boolean {
    
        const test: string = 'test';
        
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch(e) {
            return false;
        }
    }

    public static clearAllData(): void {

        localStorage.clear();
    }

    public getMode(): Mode | null {

        try {
            const stringMode: string | null = localStorage.getItem(this._id + LocalStorageKey.MODE);

            if (stringMode === null) {
                return null;
            }

            return JSON.parse(stringMode);
        } catch (e: unknown) {
            return null;
        }
    }

    public setMode(mode: Mode): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.MODE, JSON.stringify(mode));
            
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearMode(): boolean {

        try {   
            localStorage.removeItem(this._id + LocalStorageKey.MODE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getApi(): ApiService | null {

        try { 
            const baseUrl: string | null = localStorage.getItem(this._id + LocalStorageKey.API_SERVICE);

            if (baseUrl === null || baseUrl === '') {
                return null;
            }

            return new ApiService(baseUrl);
        } catch (e: unknown) {
            return null;
        }
    }

    public setApi(api: ApiService | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.API_SERVICE, api === null ? '' : api.baseUrl);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearApi(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.API_SERVICE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTheme(): AppTheme | null {

        try {
            const themeString: string | null = localStorage.getItem(this._id + LocalStorageKey.THEME);

            if (themeString === null) {
                return null;
            }

            return JSON.parse(themeString);
        } catch (e: unknown) {
            return null;
        }
    }

    public setTheme(theme: AppTheme): boolean {

        try { 
            localStorage.setItem(this._id + LocalStorageKey.THEME, JSON.stringify(theme));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTheme(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.THEME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getSystemName(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.SYSTEM_NAME);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setSystemName(systemName: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.SYSTEM_NAME, systemName ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearSystemName(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.SYSTEM_NAME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getApiVersion(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.API_VERSION);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setApiVersion(apiVersion: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.API_VERSION, apiVersion ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearApiVersion(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.API_VERSION);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getIsOffice(): boolean {

        try {
            return localStorage.getItem(this._id + LocalStorageKey.IS_OFFICE) === '1';
        } catch (e: unknown) {
            return false;
        }
    }

    public setIsOffice(isOffice: boolean): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.IS_OFFICE, isOffice ? '1' : '0');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearIsOffice(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.IS_OFFICE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getMinAppVersion(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.MIN_APP_VERSION);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setMinAppVersion(minAppVersion: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.MIN_APP_VERSION, minAppVersion ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearMinAppVersion(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.MIN_APP_VERSION);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getAppEnabled(): boolean {

        try {
            return localStorage.getItem(this._id + LocalStorageKey.APP_ENABLED) === '1';
        } catch (e: unknown) {
            return false;
        }
    }

    public setAppEnabled(appEnabled: boolean): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.APP_ENABLED, appEnabled ? '1' : '0');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearAppEnabled(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.APP_ENABLED);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getServerTimezoneOffsetMinutes(): number | null {

        try {
            return parseNumberStrict(localStorage.getItem(this._id + LocalStorageKey.SERVER_TIMEZONE_OFFSET_MINUTES));
        } catch (e: unknown) {
            return null;
        }
    }

    public setServerTimezoneOffsetMinutes(serverTimezoneOffsetMinutes: number | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.SERVER_TIMEZONE_OFFSET_MINUTES, serverTimezoneOffsetMinutes?.toString() ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearServerTimezoneOffsetMinutes(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.SERVER_TIMEZONE_OFFSET_MINUTES);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getCurrentUser(): CurrentUser | null {

        try {
            const userId: string | null = localStorage.getItem(this._id + LocalStorageKey.USER_ID);
            const username: string | null = localStorage.getItem(this._id + LocalStorageKey.USERNAME);
            const email: string | null = localStorage.getItem(this._id + LocalStorageKey.EMAIL);
            const accessString: string | null = localStorage.getItem(this._id + LocalStorageKey.ACCESS);
            
            if (userId === null || userId === '' || username === null || username === '' || email === null || accessString === null) {
                return null;
            }

            const access: string[] = accessString.split(',');
            
            return new CurrentUser(userId, username, email, access);
        } catch (e: unknown) {
            return null;
        }
    }

    public setCurrentUser(currentUser: CurrentUser | null): boolean {

        try {
            if (currentUser === null) {
                localStorage.setItem(this._id + LocalStorageKey.USER_ID, '');
                localStorage.setItem(this._id + LocalStorageKey.USERNAME, '');
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, '');
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.USER_ID, currentUser.userId);
                localStorage.setItem(this._id + LocalStorageKey.USERNAME, currentUser.username);
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, currentUser.email);
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, currentUser.access.join(',')); 
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearCurrentUser(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.USER_ID);
            localStorage.removeItem(this._id + LocalStorageKey.USERNAME);
            localStorage.removeItem(this._id + LocalStorageKey.EMAIL);
            localStorage.removeItem(this._id + LocalStorageKey.ACCESS);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getHashedPin(): string | null {

        try {
            const hashedPin: string | null = localStorage.getItem(this._id + LocalStorageKey.HASHED_PIN);

            if (hashedPin === '') {
                return null;
            }

            return hashedPin;
        } catch (e: unknown) {
            return null;
        }
    }

    public setHashedPin(hashedPin: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.HASHED_PIN, hashedPin ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearHashedPin(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.HASHED_PIN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getLastActivity(): Date | null {

        try {
            const lastActivityString: string | null = localStorage.getItem(this._id + LocalStorageKey.LAST_ACTIVITY);
            
            return datetimeStandardParser(lastActivityString, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setLastActivity(lastActivity: Date | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.LAST_ACTIVITY, lastActivity === null ? '' : datetimeToApiString(lastActivity, 0));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearLastActivity(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.LAST_ACTIVITY);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getToken(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.TOKEN);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setToken(token: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TOKEN, token ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearToken(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.TOKEN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getRefreshToken(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.REFRESH_TOKEN);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setRefreshToken(refreshToken: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.REFRESH_TOKEN, refreshToken ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearRefreshToken(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.REFRESH_TOKEN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getExpiration(): Date | null {

        try {
            const stringExpiration: string | null = localStorage.getItem(this._id + LocalStorageKey.EXPIRATION);

            return datetimeStandardParser(stringExpiration, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setExpiration(date: Date | null): boolean {

        try {
            if (date === null) {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, datetimeToApiString(date, 0));
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearExpiration(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.EXPIRATION);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTokenType(): TokenType {

        try {
            const stringTokenType: string | null = localStorage.getItem(this._id + LocalStorageKey.TOKEN_TYPE);

            if (stringTokenType === null) {
                return TokenType.TENANT;
            }

            return JSON.parse(stringTokenType);
        } catch (e: unknown) {
            return TokenType.TENANT;
        }
    }

    public setTokenType(tokenType: TokenType): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TOKEN_TYPE, JSON.stringify(tokenType));
            
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTokenType(): boolean {

        try {   
            localStorage.removeItem(this._id + LocalStorageKey.TOKEN_TYPE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTenantDisplayName(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setTenantDisplayName(tenantDisplayName: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME, tenantDisplayName ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTenantDisplayName(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }
}

export default LocalStorageService;